import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';
import Cookies from 'js-cookie';

interface RegistrationScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

interface RegistrationData {
  title_indication?: string | null;
  description_indication?: string | null;
  save_button_indication?: string | null;
  save_button_bg_color?: string | null;
  save_button_text_color?: string | null;
  save_button_shadow_color?: string | null;
  show_success_modal: boolean;
  fields: {
    name: string;
    key: string;
    field_type: string;
    required: boolean;
    options?: string[];
  }[];
}

const containerStyle = {
  margin: '0 auto',
  padding: 'clamp(8px, 2vw, 20px)',
  width: '100%',
  maxWidth: '1000px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
};

const inputStyle = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
  padding: '8px 12px',
  lineHeight: '1.5',
  display: 'flex',
  alignItems: 'center',
  height: '40px',
  minHeight: '40px',
  fontSize: '16px',
};

const textareaStyle = {
  ...inputStyle,
  alignItems: 'flex-start',
  padding: '12px',
  verticalAlign: 'top',
  height: '80px',
  minHeight: '80px',
  resize: 'vertical' as const,
};

export default function Register({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: RegistrationScreenProps) {
  const {
    backendBaseUrl,
    registerUser,
    event,
    setGlobalNotification,
    setUserEmail,
    associatePendingFeedbacks,
    style,
  } = useAppContext();
  const [registrationData, setRegistrationData] =
    useState<RegistrationData | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const eventId = event?.id;
  const firstInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );

  useEffect(() => {
    if (event?.remember_registration) {
      const rememberedEmail = Cookies.get('registeredEmail');
      if (rememberedEmail) {
        setUserEmail(rememberedEmail);
        return;
      }
    } else {
      Cookies.remove('registeredEmail');
    }
  }, [event?.remember_registration, setUserEmail]);

  useEffect(() => {
    if (eventId) {
      axios
        .get(`${backendBaseUrl}/get-registration/?event_id=${eventId}`)
        .then(res => {
          setRegistrationData(res.data);
        })
        .catch(() => {});
    }
  }, [eventId, backendBaseUrl]);

  useEffect(() => {
    if (registrationData?.fields?.length === 1 && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [registrationData]);

  const handleChange = (key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (registrationData && registrationData.fields) {
      registrationData.fields.forEach(field => {
        const value = formValues[field.key];
        if (field.required && !value) {
          newErrors[field.key] = `${field.name} is required.`;
        } else if (
          field.field_type === 'email' &&
          value &&
          !value.includes('@')
        ) {
          newErrors[field.key] = 'Invalid email address.';
        } else if (
          field.field_type === 'int' &&
          value &&
          isNaN(Number(value))
        ) {
          newErrors[field.key] = 'Must be a number.';
        } else if (
          field.field_type === 'phone' &&
          value &&
          !/^[+]?^[1-9][\d]{0,15}$/.test(value.replace(/[\s\-()]/g, ''))
        ) {
          newErrors[field.key] = 'Invalid phone number.';
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !eventId) return;
    try {
      const res = await registerUser(formValues, eventId);
      if (res.success) {
        const email = formValues.email_address || formValues.mail;
        const username = formValues.username;
        const identifier = email || username;

        if (event?.remember_registration && email) {
          Cookies.set('registeredEmail', email, { expires: 365 });
        }
        setUserEmail(identifier || '');

        if (identifier) {
          associatePendingFeedbacks(identifier);
        }
        if (registrationData?.show_success_modal) {
          setGlobalNotification(['Registration successful!', 'success']);
        }
        onNext();
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setSubmitError(err.response?.data?.error || 'Registration failed');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const btnBg =
    registrationData?.save_button_bg_color ||
    'linear-gradient(to right, #6991F8 0%, #07296a 100%)';
  const btnText = registrationData?.save_button_text_color || 'white';
  const btnShadow = registrationData?.save_button_shadow_color || '#6991F8';

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: '20px' }}>
        {registrationData?.title_indication && (
          <div
            style={{ textAlign: 'center', fontWeight: 600 }}
            dangerouslySetInnerHTML={{
              __html: registrationData.title_indication,
            }}
          />
        )}
        {registrationData?.description_indication && (
          <div
            style={{ textAlign: 'center' }}
            dangerouslySetInnerHTML={{
              __html: registrationData.description_indication,
            }}
          />
        )}
      </div>
      <div
        style={{
          overflowY: 'auto',
          marginBottom: '20px',
          flex: '1 1 auto',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch' as const,
        }}
      >
        {registrationData &&
          registrationData.fields &&
          registrationData.fields.map((field, index) => (
            <div key={field.key} style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                {field.name}
                {field.required ? ' *' : ''}
              </label>
              {field.field_type === 'text' && (
                <textarea
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLTextAreaElement>)
                      : null
                  }
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={textareaStyle}
                />
              )}
              {field.field_type === 'str' && (
                <input
                  type="text"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {field.field_type === 'email' && (
                <input
                  type="email"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {field.field_type === 'int' && (
                <input
                  type="number"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {field.field_type === 'date' && (
                <input
                  type="date"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {field.field_type === 'selection' && field.options && (
                <select
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={inputStyle}
                >
                  <option value="">Select an option</option>
                  {field.options.map((option: string) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
              {field.field_type === 'password' && (
                <input
                  type="password"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {field.field_type === 'phone' && (
                <input
                  type="tel"
                  ref={
                    index === 0
                      ? (firstInputRef as React.RefObject<HTMLInputElement>)
                      : null
                  }
                  className="register-field-input"
                  value={formValues[field.key] || ''}
                  onChange={e => handleChange(field.key, e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={inputStyle}
                />
              )}
              {errors[field.key] && (
                <div style={{ color: 'red', fontSize: '0.9em' }}>
                  {errors[field.key]}
                </div>
              )}
            </div>
          ))}
      </div>
      {submitError && (
        <div
          style={{ color: 'red', textAlign: 'center', marginBottom: '15px' }}
        >
          {submitError}
        </div>
      )}
      <button
        onClick={handleSubmit}
        style={{
          height: '50px',
          minHeight: '44px',
          marginTop: '15px',
          width: '100%',
          maxWidth: '1000px',
          borderRadius: '7px',
          padding: '0 20px',
          fontSize: '90%',
          fontWeight: 'bold',
          color: btnText,
          background: btnBg,
          border: 'none',
          cursor: 'pointer',
          boxShadow: `0 5px 5px ${btnShadow}`,
          flex: '0 0 auto',
        }}
      >
        {registrationData?.save_button_indication && (
          <span
            style={{
              display: 'inline-block',
              width: '100%',
              textAlign: 'center',
            }}
            dangerouslySetInnerHTML={{
              __html: registrationData.save_button_indication,
            }}
          />
        )}
      </button>
      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}
      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
}
